import { Music, ExternalLink } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useGenerations } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

const statusColors: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  generating_text: "bg-blue-500/10 text-blue-500",
  text_ready: "bg-yellow-500/10 text-yellow-500",
  generating_music: "bg-purple-500/10 text-purple-500",
  completed: "bg-primary/10 text-primary",
  failed: "bg-destructive/10 text-destructive",
};

const statusLabels: Record<string, string> = {
  pending: "Ожидает",
  generating_text: "🥣 Готовим текст",
  text_ready: "📝 Текст готов",
  generating_music: "🍳 Готовим музыку",
  completed: "🎂 Готово",
  failed: "❌ Ошибка",
};

export default function Generations() {
  const { data: generations, isLoading } = useGenerations();

  return (
    <AdminLayout title="Генерации" description="Все созданные хиты">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            История генераций
          </CardTitle>
          <CardDescription>Последние 100 генераций</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : !generations?.length ? (
            <p className="text-muted-foreground text-center py-8">
              Пока нет генераций
            </p>
          ) : (
            <div className="space-y-3">
              {generations.map((gen) => (
                <div key={gen.id} className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Music className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {gen.telegram_users?.username 
                            ? `@${gen.telegram_users.username}` 
                            : gen.telegram_users?.first_name || 'Пользователь'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(gen.created_at), "d MMM yyyy, HH:mm", { locale: ru })}
                        </p>
                      </div>
                    </div>
                    <Badge className={statusColors[gen.status]}>
                      {statusLabels[gen.status]}
                    </Badge>
                  </div>

                  <div className="flex gap-2 mb-2">
                    {gen.events && (
                      <Badge variant="outline">{gen.events.emoji} {gen.events.name}</Badge>
                    )}
                    {gen.genres && (
                      <Badge variant="outline">{gen.genres.emoji} {gen.genres.name}</Badge>
                    )}
                    {gen.wow_hits_used > 0 && (
                      <Badge variant="secondary">🎵 {gen.wow_hits_used} hit</Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {gen.story}
                  </p>

                  {gen.music_url && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={gen.music_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Слушать
                      </a>
                    </Button>
                  )}

                  {gen.error_message && (
                    <p className="text-xs text-destructive mt-2">{gen.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
